/**
 * GraphToolbar.tsx
 * -----------------
 * Permanent toolbar for inserting predefined graph box layouts.
 */

import React, { useState } from "react";
import {
  Button,
  Text,
  Tooltip,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { logger } from "../core/logging";
import {
  SplitHorizontalRegular,
  ArrowBetweenDownRegular,
  TableCellsSplitRegular,
  DocumentPageNumberRegular,
  TableSimpleRegular,
  NumberRowRegular,
} from "@fluentui/react-icons";
import {
  insertGraphFullLayout,
  insertGraphHalfLayout,
  insertGraphDoubleLayout,
  insertGraphHalfWithTextLayout,
  insertGraphFirstPageLayout,
  insertDoubleTableLayout,
  insertTableGraphLayout,
  insertTableLayout,
  renumberExhibits,
} from "../elements";
import { useActiveTemplate } from './hooks/useActiveTemplate';
import { ptToCm } from "../core/utils/math";
import type { GraphLayoutType } from "../templates/types";

const useStyles = makeStyles({
  container: {
    padding: "16px",
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: "12px",
  },
  title: {
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "12px",
    display: "block",
    color: tokens.colorNeutralForeground1
  },
  buttonGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  layoutButton: {
    width: "100%",
    justifyContent: "flex-start"
  },
  tooltipContent: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    backgroundColor: "#FFFAD6",
    padding: "2px 8px",
    borderRadius: "4px",
  },
  tooltipTipLabel: {
    color: "#C50F1F",
    fontWeight: "700",
  },
});

const GraphToolbar: React.FC = () => {
  const styles = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const { profile, getLayoutConfig } = useActiveTemplate();

  const formatDim = (layout: GraphLayoutType) => {
    const config = getLayoutConfig(layout);
    const h = ptToCm(config.row2Height).toFixed(2);
    const w = ptToCm(config.colWidth).toFixed(2);
    return `${h}*${w} cm`;
  };

  const formatTableDim = () => {
    const tl = profile.tableLayout;
    if (!tl) return '';
    const h = ptToCm(tl.row2Height).toFixed(2);
    const w = ptToCm(tl.totalWidth).toFixed(2);
    return `${h}*${w} cm`;
  };

  const handleLayoutChange = async (layoutFn: (profileArg: typeof profile) => Promise<void>) => {
    setIsLoading(true);
    try {
      await layoutFn(profile);
    } catch (error) {
      logger.error('Error inserting graph layout', { error });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <Text className={styles.title}>Graph/Image</Text>
      <div className={styles.buttonGroup}>
        <Tooltip content={`${formatDim('full')} (h*w)`} relationship="description">
          <Button
            icon={<SplitHorizontalRegular fontSize={16} />}
            onClick={() => handleLayoutChange(insertGraphFullLayout)}
            disabled={isLoading}
            className={styles.layoutButton}
            appearance="outline"
          >
            Full width graph
          </Button>
        </Tooltip>

        <Tooltip content={`${formatDim('double')} (h*w)`} relationship="description">
          <Button
            icon={<TableCellsSplitRegular fontSize={16} />}
            onClick={() => handleLayoutChange(insertGraphDoubleLayout)}
            disabled={isLoading}
            className={styles.layoutButton}
            appearance="outline"
          >
            Double half width graph
          </Button>
        </Tooltip>

        <Tooltip content={`${formatDim('half')} (h*w) single`} relationship="description">
          <Button
            icon={<ArrowBetweenDownRegular fontSize={16} />}
            onClick={() => handleLayoutChange(insertGraphHalfLayout)}
            disabled={isLoading}
            className={styles.layoutButton}
            appearance="outline"
          >
            Single half width graph
          </Button>
        </Tooltip>

        <Tooltip content={`${formatDim('half-with-text')} + text input`} relationship="description">
          <Button
            icon={<TableCellsSplitRegular fontSize={16} />}
            onClick={() => handleLayoutChange(insertGraphHalfWithTextLayout)}
            disabled={isLoading}
            className={styles.layoutButton}
            appearance="outline"
          >
            Half Width Graph with Text
          </Button>
        </Tooltip>

        <Tooltip content={`${formatDim('double-table')} (h*w)`} relationship="description">
          <Button
            icon={<TableCellsSplitRegular fontSize={16} />}
            onClick={() => handleLayoutChange(insertDoubleTableLayout)}
            disabled={isLoading}
            className={styles.layoutButton}
            appearance="outline"
          >
            Double half width table/image
          </Button>
        </Tooltip>

        <Tooltip content={`${formatDim('table-graph')} (h*w)`} relationship="description">
          <Button
            icon={<TableCellsSplitRegular fontSize={16} />}
            onClick={() => handleLayoutChange(insertTableGraphLayout)}
            disabled={isLoading}
            className={styles.layoutButton}
            appearance="outline"
          >
            Table/Image(Left) + Graph(Right)
          </Button>
        </Tooltip>

        {profile.id === "base" && (
          <Tooltip
            relationship="label"
            withArrow
            positioning="below"
            content={
              <div className={styles.tooltipContent}>
                <span>
                  <span className={styles.tooltipTipLabel}>Tip:</span> please move "executive summary and authors
                  elements upwards (more colser to title bg)
                </span>
                <span>Executive summary box height = 5.58 cm)</span>
                <span>Dimensions: {formatDim('first-page')} (h*w) x2</span>
              </div>
            }
            showDelay={100}
            hideDelay={100}
          >
            <Button
              icon={<DocumentPageNumberRegular fontSize={16} />}
              onClick={() => handleLayoutChange(insertGraphFirstPageLayout)}
              disabled={isLoading}
              className={styles.layoutButton}
              appearance="outline"
            >
              First Page Graphs
            </Button>
          </Tooltip>
        )}

        <Tooltip content={`${formatTableDim()} (h*w)`} relationship="description">
          <Button
            icon={<TableSimpleRegular fontSize={16} />}
            onClick={() => handleLayoutChange(insertTableLayout)}
            disabled={isLoading}
            className={styles.layoutButton}
            appearance="outline"
          >
            Full Table/Image
          </Button>
        </Tooltip>

        <Button
          icon={<NumberRowRegular fontSize={16} />}
          onClick={() => handleLayoutChange(renumberExhibits)}
          disabled={isLoading}
          className={styles.layoutButton}
          appearance="subtle"
        >
          Renumber Exhibits
        </Button>


      </div>
    </div>
  );
};

export default GraphToolbar;
